import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import AttachmentTable, { type AttachmentTableRow } from '../../../components/ui/AttachmentTable'
import FileUpload from '../../../components/ui/FileUpload'
import { GRANTS } from '../../../auth/grants'
import { useToast } from '../../../components/ui/ToastProvider'
import { formatBytes } from '../../../lib/fileSize'
import { ATTACHMENT_ACCEPT } from '../../../lib/uploadAccept'

/**
 * Tek dosya üst sınırı — backend `StorageValidator.MAX_SIZE_BYTES` ile **aynı değer** (50 MB).
 *
 * <p>Sunucu zaten reddediyor; burada da bakılmasının sebebi **zamanlama**: bu ekranda dosya hemen
 * yüklenmiyor, tutanak kaydedilirken yükleniyor. Sınır orada anlaşılsaydı kullanıcı hatayı dakikalar
 * sonra, üstelik yerel dosya seçimi kaybolmuşken görürdü — üstelik o satırın <b>geçerli</b> dosyaları
 * da aynı istekte gittiği için onlar da yüklenmemiş olurdu.
 */
const MAX_SIZE_BYTES = 50 * 1024 * 1024

interface Props {
  /** Açık satırın başlığı (prensip kararı metni); `null` → pop-up kapalı. */
  title: string | null
  files: File[]
  onChange: (files: File[]) => void
  onHide: () => void
}

/**
 * **Yüklenmeyi bekleyen dosyalar** pop-up'ı — Tutanak Oluştur ekranındaki prensip kararı satırı için (108).
 *
 * <p>{@code PrincipleFilesDialog}'un kardeşidir ve aynı kabuğu kullanır, ama <b>sunucuya hiç gitmez</b>:
 * o ekranda kayıt henüz oluşmamıştır (satırın id'si yoktur), dolayısıyla ne yükleme ne indirme ne de
 * önizleme mümkündür. Dosyalar satırın yerel durumunda birikir ve tutanak kaydedilince, sunucunun
 * döndürdüğü prensip kararı id'leriyle yüklenir.
 *
 * <p>Bu yüzden tabloda <b>İndir/Görüntüle ikonu çizilmez</b> ({@code downloadId} verilmez) — yalnız ad,
 * tür, boyut ve Kaldır vardır. Var olmayan bir kaynağa indirme düğmesi sunmak yanıltıcı olurdu.
 */
export default function PendingFilesDialog({ title, files, onChange, onHide }: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()

  /** Seçimi süzer: sınırı aşan dosya listeye girmez ve kullanıcı adıyla birlikte uyarılır. */
  const accept = (next: File[]) => {
    const tooBig = next.filter((f) => f.size > MAX_SIZE_BYTES)
    for (const f of tooBig) {
      toast.error(
        t('minuteItems.files.tooLarge', { name: f.name, max: formatBytes(MAX_SIZE_BYTES) }),
      )
    }
    onChange(next.filter((f) => f.size <= MAX_SIZE_BYTES))
  }

  // Anahtara İNDİS girer: aynı seçimde ad+boyutu eşit iki dosya olabilir (çoklu seçimde kolay bir
  // kaza) ve yalnız ad+boyutla anahtarlansaydı React yinelenen anahtar görür, Kaldır ise o anahtarla
  // eşleşen TÜM satırları silerdi — kullanıcı bir dosyayı kaldırırken ikisini birden kaybederdi
  // (Duo bulgusu MR !224).
  const rows: AttachmentTableRow[] = files.map((f, i) => ({
    key: `${i}:${f.name}:${f.size}`,
    fileName: f.name,
    extension: f.name.split('.').pop() ?? '',
    size: f.size,
  }))

  /**
   * Kaldırma <b>onay sormaz</b> — kardeş {@code PrincipleFilesDialog} sorar, çünkü orada sunucudaki
   * bir kayıt siliniyor. Burada henüz yüklenmemiş bir <b>seçim</b> geri alınıyor; dosya seçicideki
   * listeden bir satır çıkarmak gibidir ve yanlışlıkla basılırsa yeniden seçilir. Fark bilinçlidir.
   */
  const remove = (row: AttachmentTableRow) =>
    onChange(files.filter((_, i) => i !== Number(row.key.split(':')[0])))

  return (
    <FormDialog
      visible={title != null}
      header={t('minuteItems.files.title')}
      /* Footer YOK: seçim anında satıra işlenir, ayrı bir "kaydet" adımı yoktur — kapanış ×/Esc ile. */
      hideFooter
      submitLabel=""
      onSubmit={onHide}
      onHide={onHide}
      width="64rem"
    >
      {title != null && (
        <p className="ui-dialog-subject" title={title}>
          {title}
        </p>
      )}
      <div className="pd-files-upload">
        <FileUpload
          files={files}
          onChange={accept}
          accept={ATTACHMENT_ACCEPT}
          label={t('principleDecisions.files.upload')}
          hint={t('minuteItems.files.pendingHint')}
          // Yerleşik liste gizli: aşağıdaki ortak tablo aynı dosyaları tür/boyut kolonlarıyla gösteriyor.
          showList={false}
        />
      </div>
      <AttachmentTable
        rows={rows}
        emptyText={t('minuteItems.files.empty')}
        onRemove={remove}
        requiredGrant={GRANTS.PRINCIPLE_DECISION_VIEW}
      />
    </FormDialog>
  )
}
